'use client';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, type SxProps } from '@mui/material';
import { DEV_TOOLS_ENABLED } from '@repo/utils/devTools';
import { unlockDevTools } from '@repo/utils/devTools/actions';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';

const TAPS = 5;
const TAP_WINDOW_MS = 2000;

const UnlockOnTaps = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  const router = useRouter();
  const taps = useRef<number[]>([]);
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [wrong, setWrong] = useState(false);
  const [sending, setSending] = useState(false);

  const tap = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((time) => now - time < TAP_WINDOW_MS), now];
    if (taps.current.length < TAPS) return;
    taps.current = [];
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSecret('');
    setWrong(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    const unlocked = await unlockDevTools(secret);
    setSending(false);
    if (!unlocked) {
      setWrong(true);
      return;
    }
    close();
    // The root layout reads the new cookie and renders the dev panel
    router.refresh();
  };

  return (
    <>
      {/* A hidden gesture for the team, not a control: screen readers keep announcing just the logo */}
      <Box
        role="presentation"
        onClick={tap}
        sx={{ display: 'inline-flex', userSelect: 'none', WebkitTapHighlightColor: 'transparent', ...sx }}
      >
        {children}
      </Box>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <form onSubmit={submit}>
          <DialogTitle>Herramientas de desarrollo</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="Contraseña"
              autoComplete="current-password"
              value={secret}
              onChange={(event) => {
                setSecret(event.target.value);
                setWrong(false);
              }}
              error={wrong}
              helperText={wrong ? 'Contraseña incorrecta' : ' '}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={!secret || sending}>
              Desbloquear
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

/**
 * Five quick taps on the wrapped logo ask for the dev tools key; the right one unlocks the dev tools and the mock data
 * in this browser. Just the logo where there is nothing to unlock: production, and local dev, always unlocked.
 */
export const DevToolsUnlock = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) =>
  DEV_TOOLS_ENABLED && process.env.NODE_ENV !== 'development' ? (
    <UnlockOnTaps sx={sx}>{children}</UnlockOnTaps>
  ) : (
    children
  );
